<script setup lang="ts">
import type { ComponentPublicInstance, Ref } from 'vue';
import type { InputProps } from '#layers/ui/components/Input.vue';
import type { FlattenedItem, NestedSelectMenuEmits, NestedSelectMenuItem, NestedSelectMenuProps } from './types';
import { reactiveOmit, useTextDirection } from '@vueuse/core';
import { useFuse } from '@vueuse/integrations/useFuse';
import { defu } from 'defu';
import {
	ComboboxAnchor,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxPortal,
	ComboboxRoot,
	ComboboxTrigger,
	ComboboxViewport,
} from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue';
import { useAppConfig } from '#app/config';
import selectMenu from '#build/ui/select-menu';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';
import { useSubmenuSafePath } from '#layers/ui/composables/use-submenu-safe-path';
import { focusFirst } from '#layers/ui/utils/autofocus';
import { useLocale } from '#ui/composables/useLocale';
import { isArrayOfArray } from '#ui/utils';
import { provideNestedSelectMenuContext } from './NestedSelectMenuContext';
import NestedSelectMenuItemContent from './NestedSelectMenuItemContent.vue';
import NestedSelectMenuLevel from './NestedSelectMenuLevel.vue';
import { useNestedSelectMenuKeyboard } from './use-nested-select-menu-keyboard';
import { flattenWithPaths, getChildren, hasChildren, preventOutsideDismissWithinMenu, prioritizeTopLevelMatches, setExpandedItemRef, stopInputBlurDismissWithinMenu } from './utils';

const props = withDefaults(defineProps<NestedSelectMenuProps>(), {
	maxSearchDepth: 3,
	maxNestingDepth: 15,
	searchInput: true,
	closeOnSelect: true,
	portal: true,
});

const emit = defineEmits<NestedSelectMenuEmits>();

const slots = defineSlots<{
	'default'?: (props: { open: boolean }) => any;
	'item'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-leading'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-label'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-trailing'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
}>();

const modelValue = defineModel<string[]>({ default: () => [] });

const itemSlots = reactiveOmit(slots, 'default');

const appConfig = useAppConfig();
const { t } = useLocale();
const dir = useTextDirection();
const isRtl = computed(() => dir.value === 'rtl');

// Theme
const selectMenuTheme = tv({
	extend: tv(useMergedTheme({ selectMenu })),
	slots: {
		content: `
			max-h-[calc(var(--reka-combobox-content-available-height)-1rem)] w-fit min-w-fit overflow-y-auto overscroll-none
		`,
		item: 'items-center',
		itemLabel: 'text-xs',
		itemPath: '-me-1 inline-flex items-center gap-0.5 text-xs text-muted',
		group: '',
	},
	variants: {
		size: {
			// Match the select-menu `md` convention: `md` labels render at text-sm
			// (the base itemLabel slot stays text-xs for the smaller sizes).
			md: { item: 'gap-2 p-1.5 ps-2 pe-2', itemLabel: 'text-sm' },
		},
	},
});

const ui = computed(() => selectMenuTheme({
	size: props.size,
}));

const open = ref(false);
const selectionPending = ref(false);
const rootSearchTerm = ref('');
const expandedItem = ref<NestedSelectMenuItem | null>(null);
const expandedItemRef = ref<HTMLElement | null>(null);
const highlightedItem = ref<NestedSelectMenuItem | FlattenedItem | null>(null);
const submenuContentElement = ref<HTMLElement | null>(null);
const menuId = useId();

const inputRef = useTemplateRef<HTMLInputElement>('input');
const nestedMenuRef = useTemplateRef<{
	focus: () => void;
	getContentElement: () => HTMLElement | null;
}>('nestedMenuRef');
let pendingSelectTimeout: ReturnType<typeof setTimeout> | undefined;
let selectionToken = 0;

const { handleKeydown } = useNestedSelectMenuKeyboard({
	isRtl,
	highlightedItem: highlightedItem as Ref<NestedSelectMenuItem | null>,
	expandedItem: expandedItem as Ref<NestedSelectMenuItem | null>,
	nestedMenuRef,
	onEscape: () => {
		open.value = false;
	},
	onSelectHighlighted: handleHighlightedSelect,
});

const searchInputProps = computed(() =>
	defu(props.searchInput, { placeholder: props.searchPlaceholder ?? t('selectMenu.search'), variant: 'none' }) as InputProps,
);

const submenuSafePath = useSubmenuSafePath({
	submenuElement: () => submenuContentElement.value ?? nestedMenuRef.value?.getContentElement(),
	side: () => isRtl.value ? 'left' : 'right',
});

provideNestedSelectMenuContext({
	closeAll: () => {
		open.value = false;
	},
	isTargetWithinMenu,
	menuId,
});

const groups = computed((): NestedSelectMenuItem[][] => {
	if (!props.items?.length) return [];
	return isArrayOfArray(props.items) ? props.items : [props.items];
});

const flattenedItems = computed(() =>
	flattenWithPaths(props.items, props.maxSearchDepth),
);

const { results: fuzzyResults } = useFuse(
	rootSearchTerm,
	() => open.value ? flattenedItems.value : [], // only pass in flattened item if menu is actually open to prevent constant deep watcher
	{
		fuseOptions: {
			keys: ['item.label', 'searchText'],
			threshold: 0.3,
			includeScore: true,
		},
		resultLimit: 50,
		matchAllWhenSearchEmpty: false,
	},
);

const filteredFlattenedItems = computed((): FlattenedItem[] => {
	if (!rootSearchTerm.value) return [];

	if (props.searchItems) {
		return props.searchItems(rootSearchTerm.value, flattenedItems.value);
	}

	return prioritizeTopLevelMatches(
		fuzzyResults.value.map((result) => result.item),
		(item) => item.path.length === 1,
	);
});

const isSearching = computed(() => rootSearchTerm.value.length > 0);

const expandedItemChildren = computed((): NestedSelectMenuItem[] =>
	expandedItem.value ? getChildren(expandedItem.value as any) : [],
);

watch(rootSearchTerm, (term) => {
	if (term) {
		expandedItem.value = null;
		expandedItemRef.value = null;
	}
});

watch(open, (isOpen) => {
	if (!isOpen) {
		rootSearchTerm.value = '';
		expandedItem.value = null;
		expandedItemRef.value = null;
		highlightedItem.value = null;
		submenuContentElement.value = null;
		submenuSafePath.resetSafePath();
	}
});

watch(expandedItem, () => {
	submenuContentElement.value = null;
});

onBeforeUnmount(() => {
	selectionToken++;

	if (pendingSelectTimeout !== undefined) {
		clearTimeout(pendingSelectTimeout);
		pendingSelectTimeout = undefined;
	}
});

function isFlattenedItem(item: NestedSelectMenuItem | FlattenedItem): item is FlattenedItem {
	return 'item' in item && 'pathIds' in item;
}

function handleHighlight(item: NestedSelectMenuItem | FlattenedItem) {
	const menuItem = isFlattenedItem(item) ? item.item : item;

	if (menuItem.disabled) return;

	highlightedItem.value = item;
}

function handleItemMouseenter(item: NestedSelectMenuItem, event: MouseEvent) {
	if (item.disabled) return;
	if (expandedItem.value?.id !== item.id && submenuSafePath.isWithinSafePath(event)) return;

	expandedItem.value = hasChildren(item) ? item : null;
}

function commitSelect(item: NestedSelectMenuItem, path: string[]) {
	modelValue.value = path;
	emit('select', item, path);
}

async function commitSelectAfterMenuSettles(item: NestedSelectMenuItem, path: string[], closeMenuManually: boolean, token: number) {
	try {
		if (closeMenuManually) {
			open.value = false;
		}

		await nextTick();
		await new Promise((resolve) => setTimeout(resolve));

		if (token !== selectionToken) return;

		commitSelect(item, path);
	}
	finally {
		if (token === selectionToken) {
			selectionPending.value = false;
		}
	}
}

function handleSelect(item: NestedSelectMenuItem, path: string[], rootRekaSelectEvent?: Event) {
	if (!props.closeOnSelect) {
		rootRekaSelectEvent?.preventDefault();
		commitSelect(item, path);
		return;
	}

	if (selectionPending.value) {
		rootRekaSelectEvent?.preventDefault();
		return;
	}

	selectionPending.value = true;
	const token = ++selectionToken;
	// Root Reka item events close the combobox themselves; keyboard and nested selections need the manual close.
	const closeMenuManually = rootRekaSelectEvent === undefined;

	pendingSelectTimeout = setTimeout(() => {
		pendingSelectTimeout = undefined;
		void commitSelectAfterMenuSettles(item, path, closeMenuManually, token);
	});
}

function handleRootSelect(event: Event, item: NestedSelectMenuItem) {
	if (item.disabled) {
		event.preventDefault();
		return;
	}

	if (hasChildren(item)) {
		if (expandedItem.value?.id !== item.id) {
			event.preventDefault();
			expandedItem.value = item;
			return;
		}

		if (!item.selectable) {
			event.preventDefault();
			// Non-selectable parent items expand, don't select at root
			return;
		}
	}

	handleSelect(item, [item.id], event);
}

function handleHighlightedSelect(item: NestedSelectMenuItem | FlattenedItem) {
	if (isFlattenedItem(item)) {
		handleFlattenedSelect(item);
		return;
	}

	if (item.disabled) {
		return;
	}

	if (hasChildren(item)) {
		if (expandedItem.value?.id !== item.id) {
			expandedItem.value = item;
			return;
		}

		if (!item.selectable) {
			return;
		}
	}

	handleSelect(item, [item.id]);
}

function handleFlattenedSelect(flatItem: FlattenedItem, event?: Event) {
	if (flatItem.item.disabled) {
		event?.preventDefault();
		return;
	}

	handleSelect(flatItem.item, flatItem.pathIds, event);
}

function handleChildCollapse() {
	expandedItem.value = null;
	expandedItemRef.value = null;
	submenuSafePath.resetSafePath();
	focusFirst(inputRef);
}

function handleChildSelect(item: NestedSelectMenuItem, path: string[]) {
	handleSelect(item, path);
}

function handleSubmenuContentElement(element: HTMLElement | null) {
	submenuContentElement.value = element;
}

function handleExpandedItemRef(el: Element | ComponentPublicInstance | null, item: NestedSelectMenuItem) {
	setExpandedItemRef(expandedItem as any, expandedItemRef, el, item as any);
}

function handleNestedMenuOutsideEvent(event: Event) {
	preventOutsideDismissWithinMenu(event, isTargetWithinMenu);
}

function handleNestedMenuInputBlur(event: FocusEvent) {
	stopInputBlurDismissWithinMenu(event, isTargetWithinMenu);
}

function isTargetWithinMenu(target: EventTarget | null): boolean {
	const element = target instanceof Element
		? target
		: (target instanceof Node
				? target.parentElement
				: null);

	return (element?.closest('[data-nested-select-menu-id]') as HTMLElement | null)?.dataset.nestedSelectMenuId === menuId;
}
</script>

<template>
	<ComboboxRoot
		v-model:open="open"
		ignore-filter
		as-child
		:reset-search-term-on-blur="false"
		@highlight="(context) => context?.value && handleHighlight(context.value as NestedSelectMenuItem)"
	>
		<ComboboxAnchor as-child>
			<ComboboxTrigger as-child tabindex="0">
				<slot :open />
			</ComboboxTrigger>
		</ComboboxAnchor>

		<ComboboxPortal :disabled="!portal">
			<ComboboxContent
				position="popper"
				align="start"
				:side-offset="8"
				:data-nested-select-menu-id="menuId"
				:class="ui.content({ class: props.ui?.content })"
				@focus-outside="handleNestedMenuOutsideEvent"
				@interact-outside="handleNestedMenuOutsideEvent"
				@mouseenter="focusFirst(inputRef)"
				@mousemove="submenuSafePath.handlePointerMove"
				@pointer-down-outside="handleNestedMenuOutsideEvent"
			>
				<DAnimatedSheet maintain-max-size="width" class="flex min-w-full flex-col items-stretch">
					<ComboboxInput v-if="!!searchInput" v-model="rootSearchTerm" as-child>
						<DInput
							ref="input"
							autofocus
							autocomplete="off"
							:size
							v-bind="searchInputProps"
							class="sticky top-0 z-10 bg-default dark:bg-muted"
							:class="[ui.input({ class: props.ui?.input })]"
							@blur.capture="handleNestedMenuInputBlur"
							@change.stop
							@keydown="handleKeydown"
						/>
					</ComboboxInput>

					<ComboboxViewport :class="ui.viewport()">
						<!-- Empty state -->
						<ComboboxEmpty
							v-if="isSearching ? filteredFlattenedItems.length === 0 : groups.flat().length === 0"
							:class="ui.empty()"
						>
							{{ $t('common.no_results') }}
						</ComboboxEmpty>

						<!-- Search mode: flattened fuzzy results -->
						<template v-else-if="isSearching">
							<ComboboxGroup :class="ui.group({ class: props.ui?.group })">
								<ComboboxItem
									v-for="flatItem in filteredFlattenedItems"
									:key="flatItem.pathIds.join('.')"
									:value="flatItem"
									:data-nested-item="flatItem.item.id"
									:disabled="flatItem.item.disabled"
									:class="ui.item({ class: [props.ui?.item, flatItem.item.ui?.item].filter(Boolean) as string[] })"
									@select="handleFlattenedSelect(flatItem, $event)"
								>
									<DIcon
										v-if="flatItem.item.icon"
										:name="flatItem.item.icon"
										:class="ui.itemLeadingIcon({ class: flatItem.item.ui?.itemLeadingIcon })"
									/>
									<!-- Breadcrumb path -->
									<span v-if="flatItem.path.length > 1" :class="ui.itemPath({ class: props.ui?.itemPath })">
										<template v-for="part of flatItem.path.slice(0, -1)" :key="part.id">
											<span>{{ part.label }}</span>
											<DIcon
												:name="isRtl ? appConfig.ui.icons.chevronLeft : appConfig.ui.icons.chevronRight"
												class="size-3"
											/>
										</template>
									</span>
									<span :class="ui.itemLabel({ class: flatItem.item.ui?.itemLabel })">
										{{ flatItem.item.label }}
									</span>
								</ComboboxItem>
							</ComboboxGroup>
						</template>

						<!-- Normal mode: hierarchical groups -->
						<template v-else>
							<ComboboxGroup
								v-for="(group, groupIndex) in groups"
								:key="`group-${groupIndex}`"
								:class="ui.group({ class: props.ui?.group })"
							>
								<ComboboxItem
									v-for="(item, index) in group"
									:key="item.id"
									:ref="(el) => handleExpandedItemRef(el, item)"
									:value="item"
									:data-nested-item="item.id"
									:disabled="item.disabled"
									:class="[
										ui.item({ class: [props.ui?.item, item.ui?.item].filter(Boolean) as string[] }),
										expandedItem?.id === item.id && 'text-highlighted before:bg-elevated/50',
									]"
									@mouseenter="handleItemMouseenter(item, $event)"
									@select="handleRootSelect($event, item)"
								>
									<NestedSelectMenuItemContent :item :index :ui :is-rtl>
										<template v-for="(_, name) in itemSlots" #[name]="slotData" :key="name">
											<slot :name v-bind="slotData" />
										</template>
									</NestedSelectMenuItemContent>
								</ComboboxItem>
							</ComboboxGroup>
						</template>
					</ComboboxViewport>
				</DAnimatedSheet>
			</ComboboxContent>
		</ComboboxPortal>
	</ComboboxRoot>

	<!-- First nested level -->
	<NestedSelectMenuLevel
		v-if="expandedItem && expandedItemRef && !isSearching"
		ref="nestedMenuRef"
		:key="expandedItem.id"
		:items="expandedItemChildren"
		:anchor-element="expandedItemRef"
		:level="1"
		:path-so-far="[expandedItem.id]"
		:max-nesting-depth
		:search-input
		:search-placeholder
		:ui
		:is-rtl
		:portal
		@select="handleChildSelect"
		@collapse="handleChildCollapse"
		@content-element="handleSubmenuContentElement"
	>
		<template v-for="(_, name) in itemSlots" #[name]="slotData" :key="name">
			<slot :name v-bind="slotData" />
		</template>
	</NestedSelectMenuLevel>
</template>
